import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, State } from '@store/index';
import { documentationActions } from '@store/slices/documentation';
import { DocsSideNav } from '@components/Docs/DocsSideNav';

import { DocsMkDocument } from '@components/Docs/types';
import { LayoutBase } from './LayoutBase';
import { getDocumentation } from '@requests/documentation';
import { useQuery } from '@tanstack/react-query';
import { NAVBAR_TOP_HEIGHT, SIDENAV_WIDTH_PADDED } from '@components/Docs';
import { DocsSearch } from '@components/Docs/Search/DocsSearch';
import { SearchModal } from '@components/Docs/Search/SearchModal';

const Container = styled.div`
  display: grid;
  height: calc(100vh - ${NAVBAR_TOP_HEIGHT});
  width: calc(100vw - 61px);
  grid-template-columns: ${SIDENAV_WIDTH_PADDED} 1fr;
`;

interface Props {
  children: React.ReactNode;
  documentLoading: boolean;
  value: string;
  mkDocument?: DocsMkDocument | null;
}

export const LayoutDocumentation: React.FC<Props> = ({ children, mkDocument }) => {
  const dispatch = useDispatch<AppDispatch>();
  const activeTab = useSelector((state: State) => state.documentation.activeTab);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: router = [] } = useQuery({
    queryKey: ['docs'],
    queryFn: getDocumentation,
  });

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return;
      }

      // Ctrl+K/Cmd+K opens modal
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsModalOpen(true);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const setActiveTab = (val: string | null) => {
    // find the tab in the router and set it as active
    const link = router
      .map((route) => route.subsections)
      .flat()
      .find((link) => link.href === val);

    if (link === undefined) return;

    dispatch(
      documentationActions.setActiveTab({
        name: link.name,
        href: link.href as string,
        sectionId: link.section_id,
        subsectionId: link.id,
      })
    );
  };

  return (
    <LayoutBase
      header={(mkDocument?.title as string) || 'Documentation'}
      actions={
        <>
          <DocsSearch />
          <SearchModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </>
      }
      body={
        <Container>
          <DocsSideNav router={router} active={activeTab?.href as string} setActive={setActiveTab} />
          {children}
        </Container>
      }
    />
  );
};
